import {
  CopyIcon,
  ExitIcon,
  GroupIcon,
  LockClosedIcon,
  LockOpen1Icon,
  TrashIcon,
  ComponentInstanceIcon,
} from '@radix-ui/react-icons';
import { IconButton, Tooltip } from '@radix-ui/themes';
import { useTranslation } from '../../i18n';
import './SceneObjectQuickMenu.css';

export interface SceneObjectQuickMenuActions {
  locked: boolean;
  onDuplicate?: () => void;
  onCreateGroup?: () => void;
  onRemoveFromGroup?: () => void;
  onToggleLock: () => void;
  onDelete: () => void;
  onCreatePattern?: () => void;
  /** multi = selection-level toolbar (no per-object pattern/duplicate required). */
  mode?: 'single' | 'multi';
  className?: string;
  style?: React.CSSProperties;
}

function stopMenuPointer(e: React.SyntheticEvent) {
  e.stopPropagation();
}

export function SceneObjectQuickMenu({
  locked,
  onDuplicate,
  onCreateGroup,
  onRemoveFromGroup,
  onToggleLock,
  onDelete,
  onCreatePattern,
  mode = 'single',
  className,
  style,
}: SceneObjectQuickMenuActions) {
  const { t } = useTranslation();
  const isMulti = mode === 'multi';

  return (
    <div
      className={['scene-object-quick-menu', isMulti ? 'scene-object-quick-menu--selection' : '', className]
        .filter(Boolean)
        .join(' ')}
      style={style}
      role="toolbar"
      aria-label={t('stage.selectionToolbar')}
      onPointerDown={stopMenuPointer}
      onMouseDown={stopMenuPointer}
      onClick={stopMenuPointer}
      onDoubleClick={stopMenuPointer}
      onContextMenu={stopMenuPointer}
    >
      {onDuplicate && (
        <Tooltip content={t('stage.duplicate')}>
          <IconButton
            type="button"
            size="2"
            variant="soft"
            highContrast
            className="scene-object-quick-menu-btn"
            aria-label={t('stage.duplicate')}
            onClick={onDuplicate}
          >
            <CopyIcon width={18} height={18} aria-hidden="true" />
          </IconButton>
        </Tooltip>
      )}
      {onCreateGroup && (
        <Tooltip content={t('stage.createGroup')}>
          <IconButton
            type="button"
            size="2"
            variant="soft"
            highContrast
            className="scene-object-quick-menu-btn"
            aria-label={t('stage.createGroup')}
            onClick={onCreateGroup}
          >
            <GroupIcon width={18} height={18} aria-hidden="true" />
          </IconButton>
        </Tooltip>
      )}
      {onRemoveFromGroup && (
        <Tooltip content={t('stage.removeFromGroup')}>
          <IconButton
            type="button"
            size="2"
            variant="soft"
            highContrast
            className="scene-object-quick-menu-btn"
            aria-label={t('stage.removeFromGroup')}
            onClick={onRemoveFromGroup}
          >
            <ExitIcon width={18} height={18} aria-hidden="true" />
          </IconButton>
        </Tooltip>
      )}
      <Tooltip content={locked ? t('common.unlock') : t('common.lock')}>
        <IconButton
          type="button"
          size="2"
          variant="soft"
          highContrast
          className="scene-object-quick-menu-btn"
          aria-label={locked ? t('common.unlock') : t('common.lock')}
          onClick={onToggleLock}
        >
          {locked ? (
            <LockOpen1Icon width={18} height={18} aria-hidden="true" />
          ) : (
            <LockClosedIcon width={18} height={18} aria-hidden="true" />
          )}
        </IconButton>
      </Tooltip>
      <Tooltip content={t('common.delete')}>
        <IconButton
          type="button"
          size="2"
          variant="soft"
          color="red"
          highContrast
          className="scene-object-quick-menu-btn"
          aria-label={t('common.delete')}
          onClick={onDelete}
        >
          <TrashIcon width={18} height={18} aria-hidden="true" />
        </IconButton>
      </Tooltip>
      {!isMulti && (
        <Tooltip content={t('stage.createPattern')}>
          <IconButton
            type="button"
            size="2"
            variant="soft"
            highContrast
            className="scene-object-quick-menu-btn"
            aria-label={t('stage.createPattern')}
            onClick={onCreatePattern}
          >
            <ComponentInstanceIcon width={18} height={18} aria-hidden="true" />
          </IconButton>
        </Tooltip>
      )}
    </div>
  );
}
